import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { PermitForm } from "@/components/permits/permit-form";
import { useGoBack } from "@/hooks/use-go-back";

const searchSchema = z.object({
  flhaId: z.string().catch(""),
  highRiskInstanceId: z.string().catch(""),
  permitType: z.string().catch(""),
  projectId: z.string().catch(""),
});

export const Route = createFileRoute("/_authenticated/permits/new")({
  validateSearch: searchSchema,
  component: NewPermitPage,
});

const TYPE_LABELS: Record<string, string> = {
  hot_work: "Hot Work",
  confined_space: "Confined Space",
  excavation: "Excavation",
  fall_protection: "Fall Protection",
};

function NewPermitPage() {
  const navigate = useNavigate();
  const { flhaId, highRiskInstanceId, permitType, projectId } =
    Route.useSearch();
  const goBack = useGoBack(flhaId ? { to: "/flha/$flhaId", params: { flhaId } } : { to: "/" });

  if (!flhaId || !permitType) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-12 text-center">
        <p className="text-muted-foreground">
          Missing required parameters. Please create a permit from within a safety check.
        </p>
        <Button variant="outline" onClick={() => navigate({ to: "/" })}>
          Go Home
        </Button>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[permitType] || permitType;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={goBack}
          className="p-1 rounded hover:bg-muted transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-text-primary" />
        </button>
        <h1 className="text-lg font-bold text-text-primary">
          New {typeLabel} Permit
        </h1>
      </div>

      {/* Permit form */}
      <PermitForm
        permitType={permitType}
        flhaId={flhaId}
        highRiskInstanceId={highRiskInstanceId}
        projectId={projectId}
      />
    </div>
  );
}
